import { useId, useMemo, type ReactNode } from 'react';
import ReactSelect, { type StylesConfig, type GroupBase } from 'react-select';
import './Select.scss';

export interface SelectOption {
  value: string;
  label: string;
  /** Optional secondary line shown under the label in the menu (and only in
   *  the menu — the selected value still renders as the bare label). Useful
   *  for "Camera Name + camera ID" style picker rows. */
  description?: string;
  /** Per-option disable. The menu still shows the row but it can't be picked. */
  disabled?: boolean;
}

interface Props {
  options: SelectOption[];
  /** Controlled value. `''` means no selection (matches native `<select>`
   *  semantics so the migration from native is a 1-line swap). */
  value: string;
  /** Fires with the picked value, or `''` when the user clears. */
  onChange: (next: string) => void;
  /** Optional label rendered above the control. Mirrors `<Input>`. */
  label?: string;
  /** Placeholder shown when value is `''`. */
  placeholder?: string;
  /** Disable the whole control. */
  disabled?: boolean;
  /** Inline error message — overrides `hint` when both present. */
  error?: string;
  /** Inline hint shown below the control. */
  hint?: string;
  /** Override the auto-generated id (used for the label↔control pairing). */
  id?: string;
  /** Form name — enables native form submission. */
  name?: string;
  /** Show an × clear button on the right of the control. Default `false`
   *  because most use sites have a sentinel like "All …" option. */
  clearable?: boolean;
  /** Type-to-filter behaviour. Default `true` (react-select's default). */
  searchable?: boolean;
  /** `sm` = 32px field, `md` = 36px (default). Matches the rest of the UI. */
  size?: 'sm' | 'md';
  /** ARIA label when no visible `label` is rendered. */
  'aria-label'?: string;
}

/**
 * Themed wrapper around react-select. Provides the same label / error /
 * hint contract as `<Input>` so callers can swap a native `<select>` for
 * this component without thinking about styling, accessibility, or keyboard
 * behaviour — react-select handles all of that.
 *
 * Use cases not covered (intentional — keep the surface tight):
 *   - Multi-select. Add a `multi` prop + parallel value/onChange shape when
 *     the first consumer needs it.
 *   - Async / paginated options. Same — extend when a consumer asks.
 */
export const Select = ({
  options,
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  error,
  hint,
  id,
  name,
  clearable = false,
  searchable = true,
  size = 'md',
  'aria-label': ariaLabel,
}: Props) => {
  const autoId = useId();
  const controlId = id ?? `select-${name ?? autoId}`;

  // react-select wants the option object, not just the value string. We
  // translate at the boundary so callers stay in plain-value land.
  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  return (
    <div className={`ui-select ui-select--${size}${error ? ' ui-select--error' : ''}`}>
      {label && (
        <label htmlFor={controlId} className="ui-select__label">
          {label}
        </label>
      )}
      <ReactSelect<SelectOption, false, GroupBase<SelectOption>>
        inputId={controlId}
        name={name}
        options={options}
        value={selected}
        onChange={(opt) => onChange(opt ? opt.value : '')}
        isDisabled={disabled}
        isClearable={clearable}
        isSearchable={searchable}
        placeholder={placeholder ?? 'Select…'}
        aria-label={ariaLabel}
        className="ui-select__control-wrap"
        classNamePrefix="ui-select"
        isOptionDisabled={(opt) => !!opt.disabled}
        // Custom option renderer — adds the optional `description` line.
        // react-select only calls this when the menu is open, so when the
        // menu is closed the selected row uses its default label-only render.
        formatOptionLabel={renderOption}
        styles={selectStyles(!!error)}
        // Portal to body so menus aren't clipped by overflow:hidden modals,
        // table cells, drawers. menuPosition:fixed keeps positioning correct
        // when the trigger is inside a scrollable container.
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
      />
      {error ? (
        <span className="ui-select__error">{error}</span>
      ) : hint ? (
        <span className="ui-select__hint">{hint}</span>
      ) : null}
    </div>
  );
};

const renderOption = (opt: SelectOption, meta: { context: 'menu' | 'value' }): ReactNode => {
  // Only show the description inside the menu. The selected-value display
  // stays compact (label only) so the trigger height matches Input.
  if (meta.context === 'value' || !opt.description) return opt.label;
  return (
    <span className="ui-select__option">
      <span className="ui-select__option-label">{opt.label}</span>
      <span className="ui-select__option-desc">{opt.description}</span>
    </span>
  );
};

// All visual config funnels through one `styles` object so the brand
// palette is declared once. react-select v5's `classNames` API would let
// us drop these but they'd then be split across CSS + this file — single
// source of truth is more maintainable.
const selectStyles = (hasError: boolean): StylesConfig<SelectOption, false> => ({
  control: (base, state) => ({
    ...base,
    minHeight: 'var(--ui-select-h)',
    height: 'var(--ui-select-h)',
    borderRadius: 8,
    borderColor: hasError
      ? '#fca5a5'
      : state.isFocused
        ? '#1a936f'
        : '#cbd5e1',
    boxShadow: state.isFocused
      ? hasError
        ? '0 0 0 3px rgba(239, 68, 68, 0.15)'
        : '0 0 0 3px rgba(26, 147, 111, 0.15)'
      : 'none',
    backgroundColor: state.isDisabled ? '#f8fafc' : '#ffffff',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: hasError ? '#fca5a5' : '#94a3b8',
    },
  }),
  valueContainer: (base) => ({ ...base, padding: '2px 10px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: '#0f172a' }),
  singleValue: (base) => ({ ...base, color: '#0f172a' }),
  placeholder: (base) => ({ ...base, color: '#94a3b8' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: '6px',
    color: state.isDisabled ? '#cbd5e1' : '#64748b',
    '&:hover': { color: '#1a936f' },
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '6px',
    color: '#94a3b8',
    '&:hover': { color: '#ef4444' },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    boxShadow: '0 14px 40px -16px rgba(15, 23, 42, 0.32), 0 4px 12px -6px rgba(15, 23, 42, 0.12)',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 500 }),
  menuList: (base) => ({ ...base, padding: 4 }),
  option: (base, state) => ({
    ...base,
    borderRadius: 6,
    padding: '8px 10px',
    fontSize: '0.875rem',
    color: state.isDisabled ? '#cbd5e1' : '#0f172a',
    backgroundColor: state.isSelected
      ? 'rgba(26, 147, 111, 0.12)'
      : state.isFocused
        ? 'rgba(26, 147, 111, 0.06)'
        : 'transparent',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    ':active': {
      backgroundColor: 'rgba(26, 147, 111, 0.18)',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: '#94a3b8',
    fontSize: '0.8125rem',
    padding: '12px 10px',
  }),
});
